import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import {
  addExpoNotificationToken,
  fetchUserData,
  loginUser,
  logoutUser,
} from '../user/user.slice';

const FEATURE_KEY = 'authentication';

/************************************************************************************
 *
 *                                      SLICE
 *
 ************************************************************************************
 */

export interface AuthenticationState {
  token?: string;
  token_expires_in?: number;
  loggedIn: boolean;
  expoToken?: string;
}

const initialState: AuthenticationState = {
  token: undefined,
  token_expires_in: undefined,
  loggedIn: false,
  expoToken: undefined,
};

export const AuthenticationSlice = createSlice({
  name: FEATURE_KEY,
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(
        loginUser.fulfilled,
        (state: AuthenticationState, action: PayloadAction<any>) => {
          if (action.payload) {
            const token = action.payload.access_token;
            const tokenExpiresIn = action.payload.expires_in;

            state.token = token;
            state.token_expires_in = tokenExpiresIn;
            state.loggedIn = true;

            // Set Axiios defaults at login
            axios.defaults.withCredentials = true;
            axios.defaults.headers.common['Authorization'] = 'Bearer ' + token;
            axios.defaults.headers.common['Content-Type'] = 'application/json';
          }
        }
      )
      .addCase(logoutUser.fulfilled, (state: AuthenticationState) => {
        state.loggedIn = false;
        state.token = undefined;
        state.token_expires_in = undefined;
        state = initialState;
      })
      .addCase(fetchUserData.rejected, (state: AuthenticationState, action) => {
        if (action.error.message?.includes('status code 401')) {
          state.loggedIn = false;
        }
      })
      .addCase(
        addExpoNotificationToken.fulfilled,
        (state: AuthenticationState, action) => {
          state.expoToken = action.payload;
        }
      );
  },
});

/*
 * Export reducer for store configuration.
 */
const reducer = AuthenticationSlice.reducer;
const actions = AuthenticationSlice.actions;

const state = (rootState: any): AuthenticationState => rootState[FEATURE_KEY];

const tokenSelector = createSelector(state, (state) => {
  return state.token;
});

const tokenExpiresInSelector = createSelector(state, (state) => {
  return state.token_expires_in;
});

const loggedInSelector = createSelector(state, (state) => {
  return state.loggedIn === true;
});

const expoTokenSelector = createSelector(state, (state) => {
  return state.expoToken;
});

export {
  reducer,
  loggedInSelector,
  tokenSelector,
  tokenExpiresInSelector,
  expoTokenSelector,
};
